import { useCallback, useContext, useEffect, useState } from "react";
import api from "../../../../utils/axiosInstance";
import { toast } from "../../../../components/Toast/toast";
import { AuthContext } from "../../../../context/AuthContext";
import VendorPortalView from "./VendorPortalView";
import VendorPortalSettingsForm from "./VendorPortalSettingsForm";
import VendorAgreementForm from "./VendorAgreementForm";
import { VENDOR_PORTAL_EDITORS } from "./vendorPortalOptions";

const errorMessage = (err, fallback) =>
  err.response?.data?.error || err.response?.data?.message || fallback;

const VendorPortalPage = () => {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const canEdit = VENDOR_PORTAL_EDITORS.includes(user?.roleSlug);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/vendor-portal");
      setData(res.data.data);
    } catch (err) {
      console.error("Failed to load vendor portal settings", err);
      setError(errorMessage(err, "Failed to load vendor portal settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (request, fallback) => {
    try {
      const res = await request();
      setData(res.data.data);
      toast.success(res.data.message);
      return true;
    } catch (err) {
      toast.error(errorMessage(err, fallback));
      return false;
    }
  };

  const handleSaveSettings = (payload) =>
    save(() => api.put("/vendor-portal/settings", payload), "Could not save the vendor portal settings.");

  const handleSaveAgreement = (payload) =>
    save(() => api.put("/vendor-portal/agreement", payload), "Could not save the vendor agreement.");

  const handleDeleteAgreement = () =>
    save(() => api.delete("/vendor-portal/agreement"), "Could not delete the vendor agreement.");

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="h-8 w-8 animate-spin rounded-full border border-gray-300 border-t-brand-600" />
      </div>
    );
  }

  if (error) return <p className="text-sm text-danger-600">{error}</p>;

  if (editing === "settings") {
    return (
      <VendorPortalSettingsForm
        settings={data.settings}
        onSave={handleSaveSettings}
        onCancel={() => setEditing(null)}
      />
    );
  }

  if (editing === "agreement") {
    return (
      <VendorAgreementForm
        agreement={data.agreement}
        onSave={handleSaveAgreement}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <VendorPortalView
      settings={data.settings}
      agreement={data.agreement}
      canEdit={canEdit}
      onEditSettings={() => setEditing("settings")}
      onEditAgreement={() => setEditing("agreement")}
      onDeleteAgreement={handleDeleteAgreement}
    />
  );
};

export default VendorPortalPage;